import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Snackbar,
  Alert,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchLabels,
  addLabel,
  assignLabelToContact,
  createAndAssignLabel,
  clearErrors,
} from "../../../redux/Slice/Contacts/labelsSlice";
import { isSubscriptionBlockedError } from "../../../utils/subscriptionError";

const MAX_LABELS_PER_CONTACT = 5;

const LabelsDialog = ({ 
  open, 
  onClose, 
  contactIds = [], 
  isBulkOperation = false,
  onSuccess 
}) => {
  const dispatch = useDispatch();
  const { labels, isLoading, isError, errorMessage } = useSelector(
    (state) => state.labels
  );
  const { contacts } = useSelector((state) => state.contacts); // Get contacts for names

  const [selectedLabel, setSelectedLabel] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [labelError, setLabelError] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (open) {
      dispatch(fetchLabels());
    }
  }, [dispatch, open]);

  useEffect(() => {
    if (isError && errorMessage) {
      if (!isSubscriptionBlockedError(errorMessage)) {
        setSnackbar({
          open: true,
          message: errorMessage.error || errorMessage,
          severity: "error",
        });
      }
      dispatch(clearErrors());
    }
  }, [isError, errorMessage, dispatch]);

  const handleClose = () => {
    setSelectedLabel("");
    setNewLabel("");
    setLabelError("");
    onClose();
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Helper function to get contact names for better success messages
  const getContactNames = () => {
    return contactIds.map(contactId => {
      const contact = contacts.find(c => c._id === contactId);
      return contact ? contact.name : 'Unknown';
    });
  };
  
  const getContactById = (contactId) => contacts.find((c) => c._id === contactId) || null;

  const handleNewLabelChange = (e) => {
    const value = e.target.value;
    if (value && selectedLabel) {
      setSelectedLabel("");
    }
    if (value.length > 20) {
      setLabelError("Label cannot exceed 20 characters");
    } else {
      setLabelError("");
    }
    setNewLabel(value);
  };

  const handleSelectLabel = (e) => {
    const value = e.target.value;
    if (value && newLabel) {
      setNewLabel("");
      setLabelError("");
    }
    setSelectedLabel(value);
  };

  const handleSaveLabel = async () => {
    if (!contactIds.length) return;
    if (labelError) return;

    try {
      // Single-contact guard (non-bulk): enforce client-side label limit for better UX
      if (!isBulkOperation && contactIds.length === 1) {
        const contact = getContactById(contactIds[0]);
        const currentCount = Array.isArray(contact?.labels) ? contact.labels.length : 0;
        if (currentCount >= MAX_LABELS_PER_CONTACT) {
          setSnackbar({
            open: true,
            message: `Maximum ${MAX_LABELS_PER_CONTACT} labels allowed per contact`,
            severity: "warning",
          });
          return;
        }
      }

      if (newLabel) {
        // Create and assign new label
        if (isBulkOperation) {
          // For bulk operations, create the label first, then assign to each contact
          await dispatch(addLabel(newLabel)).unwrap();
          
          // Then assign to each contact
          const results = await Promise.allSettled(
            contactIds.map((contactId) =>
              dispatch(
                assignLabelToContact({
                  labelName: newLabel,
                  contactId: contactId,
                })
              ).unwrap()
            )
          );
          
          const fulfilled = results.filter((r) => r.status === "fulfilled").length;
          const rejected = results.length - fulfilled;
          
          if (fulfilled === 0) {
            const firstError = results.find((r) => r.status === "rejected")?.reason;
            throw firstError || new Error("Failed to assign label");
          }

          if (rejected > 0) {
            setSnackbar({
              open: true,
              message: `Label '${newLabel}' added to ${fulfilled} contact(s). ${rejected} contact(s) could not be updated (limit reached or already assigned).`,
              severity: "warning",
            });
          }
        } else {
          await dispatch(
            createAndAssignLabel({
              labelName: newLabel,
              contactId: contactIds[0],
            })
          ).unwrap();
        }
        // Show success message with contact name(s)
        const contactNames = getContactNames();
        let successMessage;
        if (contactIds.length === 1) {
          successMessage = `Label '${newLabel}' added to ${contactNames[0]} successfully!`;
        } else {
          successMessage = `Label '${newLabel}' added to ${contactIds.length} contacts successfully!`;
        }
        
        setSnackbar({
          open: true,
          message: successMessage,
          severity: "success",
        });
      } else if (selectedLabel) {
        // Assign existing label
        if (isBulkOperation) {
          // Assign to each contact individually
          const results = await Promise.allSettled(
            contactIds.map((contactId) =>
              dispatch(
                assignLabelToContact({
                  labelName: selectedLabel,
                  contactId: contactId,
                })
              ).unwrap()
            )
          );
          
          const fulfilled = results.filter((r) => r.status === "fulfilled").length;
          const rejected = results.length - fulfilled;
          
          if (fulfilled === 0) {
            const firstError = results.find((r) => r.status === "rejected")?.reason;
            throw firstError || new Error("Failed to assign label");
          }

          if (rejected > 0) {
            setSnackbar({
              open: true,
              message: `Label '${selectedLabel}' added to ${fulfilled} contact(s). ${rejected} contact(s) could not be updated (limit reached or already assigned).`,
              severity: "warning",
            });
          }
        } else {
          await dispatch(
            assignLabelToContact({
              labelName: selectedLabel,
              contactId: contactIds[0],
            })
          ).unwrap();
        }
        // Show success message with contact name(s)
        const contactNames = getContactNames();
        let successMessage;
        if (contactIds.length === 1) {
          successMessage = `Label '${selectedLabel}' added to ${contactNames[0]} successfully!`;
        } else {
          successMessage = `Label '${selectedLabel}' added to ${contactIds.length} contacts successfully!`;
        }
        
        setSnackbar({
          open: true,
          message: successMessage,
          severity: "success",
        });
      }

      handleClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.error || error?.message || "Failed to assign label",
        severity: "error",
      });
    }
  };


  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (newLabel || selectedLabel) && !labelError) {
      e.preventDefault();
      handleSaveLabel();
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isBulkOperation 
            ? `Add Labels to ${contactIds.length} contacts` 
            : "Add Labels to Contact"
          }
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Add New Label
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter new label name"
              value={newLabel}
              onChange={handleNewLabelChange}
              onKeyDown={handleKeyDown}
              error={!!labelError}
              helperText={labelError}
              disabled={isLoading}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Or Select Existing Label
            </Typography>
            <FormControl fullWidth>
              <Select
                value={selectedLabel}
                onChange={handleSelectLabel}
                disabled={isLoading || !labels.length}
              >
                {labels.map((label) => (
                  <MenuItem key={label} value={label}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              {!labels.length && (
                <FormHelperText>No labels available</FormHelperText>
              )}
            </FormControl>
          </Box>

          {labels.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Available Labels
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {labels.map((label) => (
                  <Chip
                    key={label}
                    label={label}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Use "Manage Labels" button to edit or delete labels
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveLabel}
            variant="contained"
            disabled={isLoading || (!newLabel && !selectedLabel) || !!labelError}
            startIcon={isLoading ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {isLoading ? "Processing..." : "Add Label"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%", maxWidth: 480 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LabelsDialog;
